import { useState, useEffect, useCallback } from 'react';
import CustomersTable from '../components/customers/CustomersTable';
import useCustomers from '../hooks/useCustomers';
import * as Icons from 'lucide-react';
import { Popover } from '@headlessui/react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { useTranslation } from '../hooks/useTranslation';
import { getDocs, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import ImportData from '../components/admin/ImportData';
import { Dialog } from '@headlessui/react';
import CreateCustomerForm from '../components/customers/CreateCustomerForm';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import toast from 'react-hot-toast';
import { logAdminAction } from '../utils/auditLogger';

export default function CustomersPage() {
  const { t } = useTranslation();
  const { customers, loading, error, refreshCustomers } = useCustomers();
  const [currentPage, setCurrentPage] = useState(() => {
    // Visszaállítjuk a lapozás állapotát a localStorage-ból, ha létezik
    const savedPage = localStorage.getItem('customersCurrentPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'username',
    direction: 'asc'
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    name: '',
    phone: '',
    dateFrom: '',
    dateTo: '',
    hasInviteCode: '',
  });
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isSendingPasswordResets, setIsSendingPasswordResets] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);

  const columns = [
    { key: 'name', label: 'Név', sortable: true },
    { key: 'primaryContact.email', label: 'Email', sortable: true },
    { key: 'primaryContact.phone', label: 'Telefon', sortable: true },
    { key: 'fullAddress', label: 'Cím', sortable: true },
    { key: 'status', label: 'Státusz', sortable: true },
    { key: 'inviteCode', label: 'Meghívó kód', sortable: true },
    { key: 'createdAt', label: 'Regisztráció', sortable: true }
  ];

  // Módosítjuk a paginate függvényt, hogy useCallback-kel kezeljük
  const paginate = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    localStorage.setItem('customersCurrentPage', pageNumber.toString());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    // Nem állítjuk vissza az első oldalra, ha rendezünk
    // setCurrentPage(1);
  };

  // Felhasználó kiválasztása/kiválasztás megszüntetése
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  // Összes felhasználó kiválasztása/kiválasztás megszüntetése az aktuális oldalon
  const toggleAllCustomersOnPage = (currentCustomers) => {
    if (selectedCustomers.length === currentCustomers.length) {
      // Ha minden felhasználó ki van választva, töröljük a kiválasztást
      setSelectedCustomers(prev => 
        prev.filter(id => !currentCustomers.some(customer => customer.id === id))
      );
    } else {
      // Különben kiválasztjuk az összes felhasználót az aktuális oldalon
      const currentIds = currentCustomers.map(customer => customer.id);
      setSelectedCustomers(prev => {
        const existingIds = prev.filter(id => !currentCustomers.some(customer => customer.id === id));
        return [...existingIds, ...currentIds];
      });
    }
  };

  // Ellenőrizzük, hogy az aktuális oldal érvényes-e
  useEffect(() => {
    if (!loading && !error) {
      const filteredCustomers = customers.filter(customer => {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = !filters.search || 
          customer.name?.toLowerCase().includes(searchTerm) ||
          customer.primaryContact?.email?.toLowerCase().includes(searchTerm) ||
          customer.primaryContact?.phone?.includes(filters.search) ||
          customer.fullAddress?.toLowerCase().includes(searchTerm);

        const matchesStatus = !filters.status || 
          customer.status?.toLowerCase() === filters.status.toLowerCase();

        const matchesName = !filters.name ||
          (customer.primaryContact?.firstName?.toLowerCase().includes(filters.name.toLowerCase()) ||
          customer.primaryContact?.lastName?.toLowerCase().includes(filters.name.toLowerCase()) ||
          customer.name?.toLowerCase().includes(filters.name.toLowerCase()));

        const matchesPhone = !filters.phone ||
          customer.primaryContact?.phone?.includes(filters.phone);

        const matchesDate = (!filters.dateFrom && !filters.dateTo) ||
          (customer.createdAt && (() => {
            const createdDate = customer.createdAt.toDate();
            const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
            const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
            
            return (!fromDate || createdDate >= fromDate) &&
                  (!toDate || createdDate <= toDate);
          })());

        const matchesInviteCode = !filters.hasInviteCode || (
          filters.hasInviteCode === 'yes' ? !!customer.inviteCode :
          filters.hasInviteCode === 'no' ? !customer.inviteCode : true
        );

        return matchesSearch && matchesStatus && matchesName && 
              matchesPhone && matchesDate && matchesInviteCode;
      });

      const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
      
      // Ha az aktuális oldal nagyobb, mint a teljes oldalak száma, és van legalább egy oldal
      if (currentPage > totalPages && totalPages > 0) {
        // Az utolsó érvényes oldalra ugrunk
        paginate(totalPages);
      }
    }
  }, [loading, error, customers, filters.search, filters.status, filters.name, filters.phone, filters.dateFrom, filters.dateTo, filters.hasInviteCode, currentPage, itemsPerPage, paginate]);

  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg">
          <p className="text-sm">Error loading customers: {error}</p>
        </div>
      </div>
    );
  }

  const filteredCustomers = customers.filter(customer => {
    const searchTerm = filters.search.toLowerCase();
    const matchesSearch = !filters.search || 
      customer.name?.toLowerCase().includes(searchTerm) ||
      customer.primaryContact?.email?.toLowerCase().includes(searchTerm) ||
      customer.primaryContact?.phone?.includes(filters.search) ||
      customer.fullAddress?.toLowerCase().includes(searchTerm);

    const matchesStatus = !filters.status || 
      customer.status?.toLowerCase() === filters.status.toLowerCase();

    const matchesName = !filters.name ||
      (customer.primaryContact?.firstName?.toLowerCase().includes(filters.name.toLowerCase()) ||
      customer.primaryContact?.lastName?.toLowerCase().includes(filters.name.toLowerCase()) ||
      customer.name?.toLowerCase().includes(filters.name.toLowerCase()));

    const matchesPhone = !filters.phone ||
      customer.primaryContact?.phone?.includes(filters.phone);

    const matchesDate = (!filters.dateFrom && !filters.dateTo) ||
      (customer.createdAt && (() => {
        const createdDate = customer.createdAt.toDate();
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
        
        return (!fromDate || createdDate >= fromDate) &&
               (!toDate || createdDate <= toDate);
      })());

    const matchesInviteCode = !filters.hasInviteCode || (
      filters.hasInviteCode === 'yes' ? !!customer.inviteCode :
      filters.hasInviteCode === 'no' ? !customer.inviteCode : true
    );

    return matchesSearch && matchesStatus && matchesName && 
           matchesPhone && matchesDate && matchesInviteCode;
  });

  // Rendezés
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    if (!sortConfig.key) return 0;

    // Kezeljük a beágyazott tulajdonságokat (pl. primaryContact.email)
    const getValue = (object, path) => {
      return path.split('.').reduce((obj, key) => obj?.[key], object);
    };

    const aValue = getValue(a, sortConfig.key);
    const bValue = getValue(b, sortConfig.key);

    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    let comparison = 0;
    if (sortConfig.key === 'createdAt') {
      comparison = aValue.toDate() - bValue.toDate();
    } else if (typeof aValue === 'number') {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  // Get unique countries for the dropdown
  const countries = [...new Set(customers.map(c => c.country).filter(Boolean))].sort();
  const statuses = [...new Set(customers.map(c => c.status).filter(Boolean))].sort();

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = sortedCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handleExport = () => {
    const exportData = filteredCustomers.map(customer => ({
      'Név': customer.name,
      'Email': customer.primaryContact?.email,
      'Telefon': customer.primaryContact?.phone,
      'Cím': customer.fullAddress,
      'Város': customer.city,
      'Irányítószám': customer.zipCode,
      'Ország': customer.country,
      'Státusz': customer.status,
      'Meghívó kód': customer.inviteCode || '-',
      'Regisztráció dátuma': customer.createdAt?.toDate().toLocaleString(),
      'Ügyfél ID': customer.customerId
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `customers_export_${new Date().toISOString().slice(0,10)}.csv`);
  };

  // Jelszó-emlékeztető küldése a kiválasztott felhasználóknak
  const handleSendPasswordResets = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Nincs kiválasztva felhasználó');
      return;
    }

    setIsSendingPasswordResets(true);
    const results = { success: 0, failed: 0, emails: [] };

    try {
      // Kiválasztott felhasználók e-mail címeinek összegyűjtése
      const selectedEmails = selectedCustomers.map(customerId => {
        const customer = customers.find(c => c.id === customerId);
        return customer?.primaryContact?.email;
      }).filter(Boolean);

      if (selectedEmails.length === 0) {
        toast.error('A kiválasztott felhasználóknak nincs e-mail címük');
        setIsSendingPasswordResets(false);
        return;
      }

      // Cloud Function hívása a jelszó-visszaállító e-mailek küldéséhez
      const response = await fetch('https://us-central1-elonyklub.cloudfunctions.net/sendPasswordResetEmails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emails: selectedEmails
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Hiba történt a jelszó-visszaállító e-mailek küldésekor');
      }

      // Eredmények feldolgozása
      for (const result of data.results) {
        const customer = customers.find(c => c.primaryContact?.email === result.email);
        
        if (result.success) {
          results.success++;
          results.emails.push(result.email);
          
          // Értesítés rögzítése az adatbázisban
          if (customer) {
            await addDoc(collection(db, `customers/${customer.id}/notifications`), {
              type: 'password_reset',
              sentAt: serverTimestamp(),
              email: result.email,
              sentBy: 'admin'
            });
            
            // Audit log
            await logAdminAction('customer.password_reset', {
              customerId: customer.id,
              email: result.email
            });
          }
        } else {
          results.failed++;
        }
      }

      // Eredmény megjelenítése
      if (results.success > 0) {
        toast.success(`${results.success} jelszó-visszaállítási e-mail sikeresen elküldve`);
      }
      
      if (results.failed > 0) {
        toast.error(`${results.failed} e-mail küldése sikertelen volt`);
      }
      
      // Kiválasztott felhasználók törlése
      setSelectedCustomers([]);
      setIsPasswordResetModalOpen(false);
    } catch (error) {
      console.error('Error sending password resets:', error);
      toast.error('Hiba történt a jelszó-visszaállítási e-mailek küldésekor');
    } finally {
      setIsSendingPasswordResets(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-500">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Left side - Title and stats */}
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold text-white">
                  {t('customers.title')}
                </h1>
                <p className="text-indigo-100">
                  {t('customers.total')}: {filteredCustomers.length} / {customers.length}
                </p>
              </div>

              {/* Right side - Actions */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Add New Customer Button */}
                <button
                  onClick={() => setIsCreateCustomerOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <Icons.UserPlus className="h-4 w-4" />
                  Új ügyfél
                </button>

                {/* Password Reset Button - Only visible when customers are selected */}
                {selectedCustomers.length > 0 && (
                  <button
                    onClick={() => setIsPasswordResetModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors inline-flex items-center gap-2"
                  >
                    <Icons.KeyRound className="h-4 w-4" />
                    Jelszó-emlékeztető küldése ({selectedCustomers.length})
                  </button>
                )}

                {/* Add Import Button */}
                <button
                  onClick={() => setIsImportOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg backdrop-blur-sm transition-colors inline-flex items-center gap-2"
                >
                  <Icons.Upload className="h-4 w-4" />
                  {t('customers.import')}
                </button>

                {/* Search */}
                <div className="relative flex-grow sm:flex-grow-0 min-w-[240px]">
                  <input
                    type="text"
                    value={filters.search}
                    onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder={t('customers.search')}
                    className="w-full pl-10 pr-4 py-2 text-sm text-white placeholder-white/60 bg-white/10 border-0 rounded-lg backdrop-blur-sm focus:ring-2 focus:ring-white/30 focus:outline-none"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icons.Search className="h-4 w-4 text-white/60" />
                  </div>
                </div>

                {/* Filters Button */}
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button 
                        className={`px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg backdrop-blur-sm transition-colors inline-flex items-center gap-2 ${
                          open || Object.values(filters).some(v => v !== '') ? 'bg-white/20' : ''
                        }`}
                      >
                        <Icons.Filter className="h-4 w-4" />
                        {t('customers.filters')}
                        {Object.values(filters).some(v => v !== '') && (
                          <span className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </Popover.Button>

                      {/* Existing Popover.Panel content */}
                      <Popover.Panel className="absolute right-0 z-10 mt-2 w-80 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="p-4 space-y-4">
                          {/* Status Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('customers.status.all')}
                            </label>
                            <select
                              value={filters.status}
                              onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
                              className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                            >
                              <option value="">Minden státusz</option>
                              <option value="Új partner">Új partner</option>
                              <option value="Aktív">Aktív</option>
                              <option value="Inaktív">Inaktív</option>
                            </select>
                          </div>

                          {/* Name Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Név
                            </label>
                            <input
                              type="text"
                              value={filters.name}
                              onChange={e => setFilters(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Név szerinti szűrés..."
                              className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                            />
                          </div>

                          {/* Phone Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Telefonszám
                            </label>
                            <input
                              type="text"
                              value={filters.phone}
                              onChange={e => setFilters(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="Telefonszám szerinti szűrés..."
                              className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                            />
                          </div>

                          {/* Date Range Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Regisztráció dátuma
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                              />
                              <input
                                type="date"
                                value={filters.dateTo}
                                onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                              />
                            </div>
                          </div>

                          {/* Invite Code Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Meghívó kód
                            </label>
                            <select
                              value={filters.hasInviteCode}
                              onChange={e => setFilters(prev => ({ ...prev, hasInviteCode: e.target.value }))}
                              className="block w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                            >
                              <option value="">Mindegy</option>
                              <option value="yes">Van meghívó kód</option>
                              <option value="no">Nincs meghívó kód</option>
                            </select>
                          </div>

                          {/* Actions */}
                          <div className="flex justify-end pt-2 border-t">
                            <button
                              onClick={() => setFilters({
                                search: '',
                                status: '',
                                name: '',
                                phone: '',
                                dateFrom: '',
                                dateTo: '',
                                hasInviteCode: '',
                              })}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                            >
                              <Icons.RotateCcw className="h-4 w-4" />
                              {t('customers.resetAll')}
                            </button>
                          </div>
                        </div>
                      </Popover.Panel>
                    </>
                  )}
                </Popover>

                {/* Export Button */}
                <button 
                  onClick={handleExport}
                  className="px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg backdrop-blur-sm transition-colors inline-flex items-center gap-2"
                >
                  <Icons.Download className="h-4 w-4" />
                  {t('customers.export')}
                </button>
              </div>
            </div>

            {/* Status filters */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                className={`px-3 py-1.5 text-sm font-medium rounded-full backdrop-blur-sm transition-colors ${
                  !filters.status 
                    ? 'bg-white text-indigo-600' 
                    : 'text-white bg-white/10 hover:bg-white/20'
                }`}
              >
                {t('customers.status.all')}
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, status: 'active' }))}
                className={`px-3 py-1.5 text-sm font-medium rounded-full backdrop-blur-sm transition-colors ${
                  filters.status === 'active'
                    ? 'bg-white text-indigo-600'
                    : 'text-white bg-white/10 hover:bg-white/20'
                }`}
              >
                {t('customers.status.active')}
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, status: 'inactive' }))}
                className={`px-3 py-1.5 text-sm font-medium rounded-full backdrop-blur-sm transition-colors ${
                  filters.status === 'inactive'
                    ? 'bg-white text-indigo-600'
                    : 'text-white bg-white/10 hover:bg-white/20'
                }`}
              >
                {t('customers.status.inactive')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <Dialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <Dialog.Title className="text-lg font-medium">
                {t('customers.importTitle')}
              </Dialog.Title>
              <button
                onClick={() => setIsImportOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-100">
              <div className="flex items-start gap-3">
                <Icons.AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Figyelem: Csak szakértőknek!</h3>
                  <p className="mt-1 text-sm text-amber-700">
                    Az importálás funkció használata speciális ismereteket igényel. Helytelen használat esetén adatvesztés vagy duplikáció történhet. Csak akkor használja, ha pontosan tudja, mit csinál.
                  </p>
                </div>
              </div>
            </div>
            
            <ImportData />
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Create Customer Modal */}
      <Dialog
        open={isCreateCustomerOpen}
        onClose={() => setIsCreateCustomerOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-xl shadow-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <Dialog.Title className="text-lg font-medium">
                Új ügyfél létrehozása
              </Dialog.Title>
              <button
                onClick={() => setIsCreateCustomerOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>
            
            <CreateCustomerForm 
              onClose={() => setIsCreateCustomerOpen(false)} 
              onSuccess={refreshCustomers}
            />
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Password Reset Confirmation Modal */}
      <Dialog
        open={isPasswordResetModalOpen}
        onClose={() => !isSendingPasswordResets && setIsPasswordResetModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <Dialog.Title className="text-lg font-medium">
                Jelszó-emlékeztető küldése
              </Dialog.Title>
              <button
                onClick={() => !isSendingPasswordResets && setIsPasswordResetModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
                disabled={isSendingPasswordResets}
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-amber-100 rounded-full">
                  <Icons.KeyRound className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 text-center">
                  Jelszó-emlékeztető küldése
                </h3>
                <p className="mt-2 text-sm text-gray-500 text-center">
                  {selectedCustomers.length === 1 
                    ? 'Biztosan küldeni szeretne jelszó-emlékeztetőt a kiválasztott ügyfélnek?' 
                    : `Biztosan küldeni szeretne jelszó-emlékeztetőt ${selectedCustomers.length} kiválasztott ügyfélnek?`}
                </p>
              </div>

              <div className="mt-4 max-h-40 overflow-y-auto">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Kiválasztott ügyfelek:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {selectedCustomers.map(customerId => {
                    const customer = customers.find(c => c.id === customerId);
                    return customer ? (
                      <li key={customerId} className="flex items-center">
                        <Icons.Check className="h-4 w-4 text-green-500 mr-2" />
                        {customer.name} ({customer.primaryContact?.email || 'Nincs e-mail'})
                      </li>
                    ) : null;
                  })}
                </ul>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsPasswordResetModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isSendingPasswordResets}
                >
                  Mégse
                </button>
                <button
                  type="button"
                  onClick={handleSendPasswordResets}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 flex items-center"
                  disabled={isSendingPasswordResets}
                >
                  {isSendingPasswordResets ? (
                    <>
                      <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Küldés folyamatban...
                    </>
                  ) : (
                    <>
                      <Icons.Send className="h-4 w-4 mr-2" />
                      Jelszó-emlékeztető küldése
                    </>
                  )}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Main content */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Selection info bar */}
        {selectedCustomers.length > 0 && (
          <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center">
              <Icons.CheckSquare className="h-5 w-5 text-indigo-500 mr-2" />
              <span className="text-sm text-indigo-700">
                {selectedCustomers.length} ügyfél kiválasztva
              </span>
            </div>
            <button
              onClick={() => setSelectedCustomers([])}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              <Icons.X className="h-4 w-4 mr-1" />
              Kiválasztás törlése
            </button>
          </div>
        )}

        <CustomersTable 
          customers={currentCustomers}
          onSort={handleSort}
          sortConfig={sortConfig}
          searchTerm={filters.search}
          onRefresh={refreshCustomers}
          selectedCustomers={selectedCustomers}
          onToggleCustomer={toggleCustomerSelection}
          onToggleAll={() => toggleAllCustomersOnPage(currentCustomers)}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-4 sm:mb-0">
              <span>Megjelenítve: </span>
              <span className="font-medium">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredCustomers.length)}</span>
              <span> / </span>
              <span className="font-medium">{filteredCustomers.length}</span>
              <span> rekord</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md border ${
                  currentPage === 1 
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-indigo-300'
                }`}
                aria-label="Első oldal"
              >
                <Icons.ChevronsLeft className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md border ${
                  currentPage === 1 
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-indigo-300'
                }`}
                aria-label="Előző oldal"
              >
                <Icons.ChevronLeft className="h-4 w-4" />
              </button>

              <div className="flex space-x-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`w-10 h-10 flex items-center justify-center rounded-md border text-sm font-medium ${
                          currentPage === pageNumber
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-indigo-300'
                        }`}
                        aria-label={`${pageNumber}. oldal`}
                        aria-current={currentPage === pageNumber ? 'page' : undefined}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return (
                      <span 
                        key={pageNumber} 
                        className="w-10 h-10 flex items-center justify-center text-gray-500"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md border ${
                  currentPage === totalPages 
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-indigo-300'
                }`}
                aria-label="Következő oldal"
              >
                <Icons.ChevronRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md border ${
                  currentPage === totalPages 
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-indigo-300'
                }`}
                aria-label="Utolsó oldal"
              >
                <Icons.ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}